
import React, { useEffect, useState } from 'react';
import { Bell, Check, X } from 'lucide-react';
import { 
  getPendingInvitationsForEmail, 
  acceptInvitation, 
  declineInvitation 
} from '@/utils/firestore/invitations/invitationService';
import { getAuth } from 'firebase/auth';
import { useToast } from '@/hooks/use-toast';
import { Invitation } from '@/types/schema';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const PendingInvitationsAlert: React.FC = () => {
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  const auth = getAuth();
  const { toast } = useToast();
  
  useEffect(() => {
    const loadPendingInvitations = async () => {
      if (!auth.currentUser?.email) return;
      
      setLoading(true);
      try {
        const invitations = await getPendingInvitationsForEmail(auth.currentUser.email);
        setPendingInvitations(invitations);
        
        // Auto-open if there are invitations
        if (invitations.length > 0) {
          setOpen(true);
        }
      } catch (error) {
        console.error('Error loading pending invitations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadPendingInvitations();
    
    // Reload invitations when user changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadPendingInvitations();
      } else {
        setPendingInvitations([]);
      }
    });
    
    return () => unsubscribe();
  }, [auth]);
  
  const handleAccept = async (invitationId: string) => {
    if (!auth.currentUser?.uid) return;
    
    setProcessingIds(prev => [...prev, invitationId]);
    
    try {
      await acceptInvitation(invitationId, auth.currentUser.uid);
      
      toast({
        title: 'Success',
        description: 'Invitation accepted successfully'
      });
      
      // Remove from list
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: 'Error',
        description: 'Failed to accept invitation',
        variant: 'destructive'
      });
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== invitationId));
    }
  };
  
  const handleDecline = async (invitationId: string) => {
    setProcessingIds(prev => [...prev, invitationId]);
    
    try {
      await declineInvitation(invitationId);
      
      toast({
        title: 'Success',
        description: 'Invitation declined'
      });
      
      // Remove from list
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast({
        title: 'Error',
        description: 'Failed to decline invitation',
        variant: 'destructive'
      });
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== invitationId));
    }
  };
  
  // Don't render anything if no pending invitations
  if (pendingInvitations.length === 0) {
    return null;
  }
  
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="w-full mb-4"
    >
      <CollapsibleTrigger asChild>
        <Alert className="cursor-pointer">
          <Bell className="h-4 w-4" />
          <AlertTitle>Pending Invitations</AlertTitle>
          <AlertDescription>
            You have {pendingInvitations.length} pending invitation{pendingInvitations.length > 1 ? 's' : ''}
          </AlertDescription>
        </Alert>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2 mt-2">
          {pendingInvitations.map(invitation => (
            <Card key={invitation.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">
                    {invitation.entityType === 'portfolio' ? 'Portfolio' : 'Business'} Invitation
                  </CardTitle>
                  <Badge>{invitation.role}</Badge>
                </div>
                <CardDescription>
                  Invited by {invitation.invitedByName || 'someone'} on {new Date(invitation.invitedAt).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-2">
                <p className="text-sm">
                  You've been invited to join a {invitation.entityType} with these permissions:
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {invitation.permissions.map(permission => (
                    <Badge key={permission.id} variant="outline">
                      {permission.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="pt-2 flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDecline(invitation.id)}
                  disabled={processingIds.includes(invitation.id)}
                >
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAccept(invitation.id)}
                  disabled={processingIds.includes(invitation.id)}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Accept
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default PendingInvitationsAlert;
